import { useEffect, useRef, useState } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import { useCompany } from '@/contexts/CompanyContext';
import { useActiveOrgId } from '@/contexts/NetClientsContext';
import { useNetUser } from '@/hooks/useNetUser';
import { pickRequestedOrg } from '@/hooks/activeOrgFromQuery.utils';

/**
 * Applies `?org=<id>` to the active org BEFORE the routes below it render.
 *
 * Sits outside `ProtectedRoute` on purpose: the guard checks permissions against
 * the active org, so a deep link to another company would bounce to `/` before
 * the page ever mounted. This holds the outlet (renders nothing) until the
 * switch is applied and `/me` has resolved for the new org — the same window
 * `ProtectedRoute` itself waits out on a fresh load.
 *
 * Applies each requested id once. A later manual switch in the company picker
 * is respected even though the param is still in the URL.
 */
export function ActiveOrgFromQuery() {
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('org');
  const activeOrgId = useActiveOrgId();
  const { companies, isLoadingCompanies, setSelectedCompany } = useCompany();
  const { isLoading: isUserLoading } = useNetUser();

  const appliedRef = useRef<string | null>(null);
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);

  const unhandled = requested !== null && appliedRef.current !== requested;
  const target = unhandled && !isLoadingCompanies ? pickRequestedOrg(requested, activeOrgId, companies) : null;

  useEffect(() => {
    if (!unhandled || isLoadingCompanies) return;
    appliedRef.current = requested;
    if (target) {
      setPendingOrgId(target.id);
      setSelectedCompany(target);
    }
  }, [unhandled, isLoadingCompanies, requested, target, setSelectedCompany]);

  // Once the switch has landed, stop holding — otherwise a later manual switch in
  // the company picker would blank this route for good.
  const settled = pendingOrgId !== null && activeOrgId === pendingOrgId && !isUserLoading;
  useEffect(() => {
    if (settled) setPendingOrgId(null);
  }, [settled]);

  // Decided synchronously in render, not in the effect above: a `<Navigate>` in a
  // child guard would run its effect before ours and win the race.
  if (unhandled && isLoadingCompanies) return null;
  if (target) return null;
  if (pendingOrgId !== null && (activeOrgId !== pendingOrgId || isUserLoading)) return null;

  return <Outlet />;
}
